import React, { useEffect, useState } from "react";
import { Box, Checkbox, Chip } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import TextField from "components/TextField";
import { ForecastTemplateData } from "api/models/Forecast";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

interface SavedSampleSelectProps {
  savedSamples: ForecastTemplateData[];
  selectedSavedSamples?: { title: string; value: number }[];
  setSelectedSavedSamples?: (
    savedSamples: { title: string; value: number }[],
  ) => void;
  formValues?: any;
  setFormValues?: React.Dispatch<React.SetStateAction<any>>;
  minWidth?: number;
  maxWidth?: number;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
}

const SavedSampleSelect: React.FC<SavedSampleSelectProps> = ({
  savedSamples,
  selectedSavedSamples,
  setSelectedSavedSamples,
  formValues,
  setFormValues,
  minWidth = 480,
  maxWidth = 480,
  label = "Saved samples",
  placeholder = "Select saved samples",
  disabled,
}) => {
  const [savedSampleOptions, setSavedSampleOptions] = useState<
    { title: string; value: number }[]
  >([]);

  useEffect(() => {
    if (savedSamples) {
      const savedSampleOptionsCopy = savedSamples.map((ss) => {
        return { title: ss.name, value: ss.id };
      });

      setSavedSampleOptions(savedSampleOptionsCopy);
    }
  }, [savedSamples]);

  const handleSelectSavedSample = (option: {
    title: string;
    value: number;
  }) => {
    let selectedSavedSamplesData = [...selectedSavedSamples];

    if (!!selectedSavedSamples.find((sc) => sc.value === option.value)) {
      selectedSavedSamplesData = selectedSavedSamplesData.filter(
        (sc) => sc.value !== option.value,
      );
    } else {
      selectedSavedSamplesData = [...selectedSavedSamplesData, option];
    }

    setSelectedSavedSamples(selectedSavedSamplesData);
    if (setFormValues) {
      setFormValues({
        ...formValues,
        selectedSavedSamples: selectedSavedSamplesData,
      });
    }
  };

  const handleRemoveSelectedSavedSample = (option: {
    title: string;
    value: number;
  }) => {
    let selectedSavedSamplesData = [...selectedSavedSamples];
    selectedSavedSamplesData = selectedSavedSamplesData.filter(
      (sc) => sc.title !== option.title,
    );
    setSelectedSavedSamples(selectedSavedSamplesData);
    if (setFormValues) {
      setFormValues({
        ...formValues,
        selectedSavedSamples: selectedSavedSamplesData,
      });
    }
  };

  return (
    <Box sx={{ mr: "0.5rem" }}>
      <Autocomplete
        multiple
        id="selectedSavedSamples"
        value={selectedSavedSamples}
        limitTags={2}
        disabled={disabled}
        disableCloseOnSelect
        renderTags={(value, getTagProps) =>
          value?.map((option, index) => {
            const { key } = getTagProps({ index });
            return (
              <Chip
                label={option.title}
                key={key}
                onDelete={() => handleRemoveSelectedSavedSample(option)}
                size="small"
                sx={{ mr: "4px", mb: "4px" }}
              />
            );
          })
        }
        options={savedSampleOptions || []}
        getOptionLabel={(option) => option.title}
        defaultValue={[]}
        size="small"
        renderInput={(params) => (
          <TextField
            id="savedSample"
            params={params}
            minWidth={minWidth}
            maxWidth={maxWidth}
            label={label}
            placeholder={placeholder}
          />
        )}
        renderOption={(props, option) => {
          const isSelected = !!selectedSavedSamples.find(
            (sc) => sc.value === option.value,
          );

          return (
            <li
              {...props}
              style={{ padding: "0rem" }}
              onClick={() => handleSelectSavedSample(option)}
            >
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 4 }}
                checked={isSelected}
              />
              {option.title}
            </li>
          );
        }}
      />
    </Box>
  );
};

export default SavedSampleSelect;
